import React from "react";
import { classNames } from "./utils/classNames";

export const TextIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24" fill="none">
    <path
      d="M.75 12.749h10.5m4.5 4.5h7.5m-7.5 4.5V13.5a3.75 3.75 0 1 1 7.5 0v8.25m-22.5-.001V7.5a5.25 5.25 0 0 1 10.5 0v14.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PlayIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    {...props}
    // Some manipulation to make the icon appear roughly in the right size and position as the others
    viewBox="-2 0 28 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
  >
    <path d="M2.33801 3.25499H3.08801V3.25492L2.33801 3.25499ZM2.33801 20.745L3.08801 20.7451V20.745H2.33801ZM4.54701 22.067L4.19283 21.4059L4.19251 21.4061L4.54701 22.067ZM20.87 13.322L21.2242 13.9831L21.2244 13.983L20.87 13.322ZM20.87 10.678L21.2244 10.017L21.2242 10.0169L20.87 10.678ZM4.54701 1.93299L4.19251 2.59392L4.19283 2.59409L4.54701 1.93299ZM1.58801 3.25499V20.745H3.08801V3.25499H1.58801ZM1.58801 20.7449C1.58794 21.5362 2.00357 22.2694 2.68258 22.6758L3.45287 21.3887C3.22653 21.2532 3.08799 21.0088 3.08801 20.7451L1.58801 20.7449ZM2.68258 22.6758C3.3616 23.0822 4.20417 23.102 4.90151 22.7279L4.19251 21.4061C3.96006 21.5307 3.67921 21.5241 3.45287 21.3887L2.68258 22.6758ZM4.9012 22.7281L21.2242 13.9831L20.5158 12.6609L4.19283 21.4059L4.9012 22.7281ZM21.2244 13.983C21.9552 13.5912 22.4113 12.8292 22.4113 12H20.9113C20.9113 12.2764 20.7592 12.5304 20.5156 12.661L21.2244 13.983ZM22.4113 12C22.4113 11.1708 21.9552 10.4088 21.2244 10.017L20.5156 11.339C20.7592 11.4696 20.9113 11.7236 20.9113 12H22.4113ZM21.2242 10.0169L4.9012 1.27189L4.19283 2.59409L20.5158 11.3391L21.2242 10.0169ZM4.90151 1.27206C4.20417 0.898028 3.3616 0.91782 2.68258 1.32418L3.45287 2.6113C3.67921 2.47584 3.96006 2.46924 4.19251 2.59392L4.90151 1.27206ZM2.68258 1.32418C2.00357 1.73055 1.58794 2.46374 1.58801 3.25506L3.08801 3.25492C3.08799 2.99115 3.22653 2.74675 3.45287 2.6113L2.68258 1.32418Z" />
  </svg>
);

export const CircleIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg strokeWidth={2} fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
    <circle cx="12" cy="12" r="9" />
  </svg>
);

interface FractionalCircleIcon extends React.ComponentProps<"svg"> {
  fraction: number;
}

export const FractionalCircleIcon = (props: FractionalCircleIcon): JSX.Element => {
  const { fraction, className, ...rest } = props;
  const radius = 9;
  const circumference = 2 * radius * Math.PI;
  const arcLength = circumference * fraction;
  return (
    <svg
      {...rest}
      className={classNames(className, "-rotate-90 transition-all")}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle
        cx="12"
        cy="12"
        r={radius}
        strokeDasharray={`${arcLength} 9999`} // arclength followed by big gap
      />
      <circle cx="12" cy="12" r={radius} className="opacity-10" />
    </svg>
  );
};

export const LoadingIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  const { className, ...rest } = props;
  return (
    <svg className={classNames("animate-spin", className)} {...rest} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

// NB: This looks *really bad* in Safari on non-retina displays. Have tried webkit-optimize-contrast: optimizeQuality css, but that didn't help.
export const Lissajous2Icon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
    <path
      d="M4.33937 16.0005L4.34703 16.5379L4.37157 17.5431L4.40841 18.4557L4.45755 19.2756L4.48674 19.6508L4.52708 20.1174L4.60703 20.9274L4.70349 21.6732L4.83749 22.475L4.92513 22.9344L4.99916 23.3069L5.1386 23.9545L5.29606 24.5461L5.50695 25.1698L5.64348 25.5213L5.74521 25.7715L5.89115 26.1077L5.99155 26.3117L6.10048 26.5046L6.22312 26.6905L6.36463 26.8735L6.5302 27.0578L6.62363 27.1517L6.69133 27.2155L6.8283 27.3314L6.96829 27.431L7.11238 27.5138L7.26166 27.5791L7.41723 27.6264L7.58017 27.655L7.75157 27.6644L7.84079 27.6618L7.94606 27.6532L8.14411 27.6216L8.32856 27.5715L8.50308 27.5037L8.67136 27.419L8.83706 27.3181L9.08884 27.1381L9.2642 27.0002L9.39571 26.8923L9.63278 26.6851L9.84202 26.4845L10.0315 26.2845L10.2966 25.9727L10.6567 25.504L10.8579 25.2335L11.124 24.8693L11.5675 24.2224L11.9533 23.607L12.3535 22.93L12.5816 22.5392L12.8511 22.0733L13.3063 21.2575L13.9198 20.0967L14.3917 19.188L14.897 18.2055L15.7466 16.5041L17.3615 13.2882L17.8187 12.4019L18.0871 11.8937L18.5505 11.0206L19.1567 9.9073L19.6099 9.1277L19.879 8.68341L20.1006 8.32159L20.4909 7.69478L20.8699 7.1279L21.3087 6.5379L21.5729 6.2083L21.7665 5.97214L22.1168 5.56262L22.3766 5.29455L22.5624 5.12688L22.7671 4.96335L22.9982 4.79993L23.1259 4.717L23.2976 4.61355L23.5482 4.48554L23.7136 4.42013L23.88 4.37213L24.0494 4.34279L24.2237 4.33337L24.4049 4.34512L24.4988 4.35933L24.5914 4.37861L24.7648 4.43062L24.9243 4.5001L25.0722 4.58655L25.2109 4.68943L25.3426 4.8082L25.5323 5.01502L25.6569 5.17123L25.746 5.29081L25.9028 5.51946L26.0355 5.73931L26.1493 5.95663L26.2963 6.29157L26.4761 6.78772L26.5709 7.07114L26.6945 7.45593L26.8853 8.13385L27.0271 8.7716L27.1519 9.46758L27.2178 9.86817L27.2931 10.347L27.4074 11.1815L27.4874 11.9564L27.5505 12.797L27.5811 13.2813L27.6112 13.8015L27.6511 14.7055L27.6667 15.5416L27.6612 16.4466L27.6516 16.9676L27.6398 17.4758L27.6088 18.3582L27.5594 19.1725L27.4811 20.0515L27.4277 20.5568L27.378 21.0026L27.2817 21.7767L27.169 22.4886L27.0151 23.2519L26.915 23.6882L26.8332 24.0305L26.6796 24.6267L26.5538 25.0341L26.4559 25.3046L26.341 25.5855L26.2043 25.8863L26.1263 26.0472L26.0174 26.264L25.8582 26.5564L25.7481 26.7325L25.6295 26.8959L25.4979 27.0486L25.3486 27.1926L25.1772 27.33L25.0817 27.3969L25.0067 27.4447L24.8557 27.5261L24.7029 27.5888L24.5478 27.6327L24.3899 27.6579L24.2287 27.6647L24.0636 27.6532L23.8941 27.6233L23.8076 27.6015L23.6922 27.5673L23.4795 27.4894L23.2864 27.3985L23.1076 27.294L22.9378 27.1753L22.7714 27.042L22.5169 26.8132L22.3361 26.6407L22.2021 26.5096L21.9607 26.2615L21.7478 26.026L21.5547 25.7952L21.2839 25.4413L20.9131 24.9147L20.7045 24.6108L20.437 24.2152L19.9894 23.5166L19.3995 22.5092L18.9539 21.7159L18.6845 21.2317L18.2279 20.3855L17.6082 19.185L17.1288 18.2467L16.626 17.2561L15.775 15.544L15.2751 14.5519L14.7901 13.5936L14.166 12.3662L13.7089 11.4992L13.4397 11.0026L12.9856 10.1708L12.3879 9.11234L11.9375 8.37462L11.6691 7.95546L11.4544 7.62451L11.0746 7.05125L10.7029 6.53637L10.2695 6.00813L10.0079 5.71658L9.82158 5.51509L9.56313 5.24717L9.39541 5.08842L9.22377 4.94314L9.04171 4.80927L8.84272 4.68473L8.6203 4.56747L8.49827 4.51091L8.41257 4.47456L8.24376 4.41374L8.07774 4.36948L7.9137 4.34261L7.75083 4.33399L7.58831 4.34445L7.42533 4.37483L7.26109 4.42597L7.17824 4.45959L7.0831 4.50398L6.90945 4.60182L6.75487 4.7122L6.6157 4.83566L6.48824 4.97276L6.36881 5.12403L6.19667 5.37869L6.0812 5.56776L5.99784 5.71082L5.85163 5.98034L5.72852 6.23455L5.62338 6.48163L5.48811 6.85672L5.32272 7.40808L5.2349 7.7241L5.12415 8.13848L4.95353 8.8652L4.82744 9.54519L4.71796 10.2856L4.66071 10.712L4.59745 11.2067L4.50285 12.068L4.43947 12.8666L4.3937 13.7324L4.37316 14.2311L4.35434 14.7533L4.33333 15.6605L4.33473 16.4989L4.35885 17.406L4.37952 17.9281L4.40174 18.4246L4.45041 19.2867L4.51654 20.0817L4.61437 20.939L4.67962 21.4314L4.73832 21.8536L4.85036 22.587L4.97907 23.2603L5.15301 23.9793L5.26587 24.389L5.35487 24.6997L5.52259 25.242L5.65976 25.6106L5.76634 25.8529L5.89105 26.1016L6.03909 26.3646L6.12346 26.5039L6.24033 26.6875L6.41522 26.9345L6.53662 27.0806L6.66592 27.2123L6.80657 27.3298L6.96207 27.4334L7.13587 27.5235L7.23072 27.5635L7.3153 27.5941L7.48229 27.639L7.64722 27.6629L7.81101 27.6667L7.97463 27.6514L8.13903 27.618L8.30515 27.5674L8.47395 27.5006L8.55965 27.4614L8.68278 27.4004L8.90684 27.2754L9.10687 27.1443L9.28957 27.0047L9.54575 26.7743L9.88913 26.4151L10.0767 26.2078L10.3387 25.9098L10.773 25.371L11.146 24.8473L11.5275 24.2649L11.7434 23.9288L12.012 23.5052L12.4629 22.7602L13.0621 21.6921L13.5178 20.8531L13.7868 20.3543L14.244 19.4837L14.8689 18.2517L15.3549 17.29L15.8542 16.2982L16.705 14.587L17.2083 13.5972L17.6865 12.6634L18.3053 11.469L18.7617 10.6274L19.0312 10.146L19.475 9.36072L20.0632 8.36393L20.5102 7.67355L20.7775 7.28292L20.9849 6.98444L21.3539 6.46734L21.6238 6.12031L21.8163 5.89452L22.0287 5.66457L22.2694 5.42308L22.403 5.29569L22.5829 5.1289L22.8368 4.90801L23.003 4.77987L23.1725 4.66666L23.3507 4.56812L23.5424 4.48394L23.7529 4.41383L23.8668 4.38397L23.9537 4.36522L24.1234 4.34167L24.2882 4.33667L24.4487 4.3502L24.6053 4.38221L24.7587 4.43268L24.9094 4.50156L25.058 4.58882L25.1316 4.63933L25.227 4.71071L25.3977 4.85598L25.5458 5.00658L25.6759 5.16488L25.7928 5.33325L25.9012 5.51407L26.058 5.81383L26.1657 6.03614L26.2426 6.20037L26.3774 6.5071L26.4906 6.79308L26.5871 7.06813L26.711 7.48171L26.8624 8.0866L26.9428 8.43406L27.0409 8.87445L27.1915 9.64444L27.3016 10.3623L27.3952 11.1427L27.4434 11.5922L27.4948 12.0993L27.5699 12.9813L27.6165 13.7982L27.6445 14.6834L27.6547 15.1932L27.6624 15.7137L27.6648 16.6177L27.6465 17.4529L27.6034 18.3558L27.5714 18.8753L27.5392 19.3568L27.4733 20.1928L27.3905 20.9632L27.273 21.7927L27.1957 22.2686L27.1284 22.6647L27.0012 23.3529L26.8567 23.9833L26.6627 24.6526L26.5369 25.0321L26.441 25.3101L26.2587 25.797L26.1097 26.1251L25.9944 26.3373L25.8602 26.5513L25.7018 26.7728L25.6119 26.8883L25.4856 27.0391L25.2928 27.2377L25.159 27.3507L25.0185 27.4476L24.8692 27.5277L24.7091 27.5904L24.536 27.6351L24.4439 27.6505L24.3481 27.6612L24.164 27.6661L23.9879 27.65L23.8176 27.6141L23.6508 27.5599L23.4853 27.4884L23.2346 27.3515L23.0624 27.2424L22.934 27.1552L22.7018 26.9843L22.4962 26.8145L22.3098 26.6414L22.0492 26.366L21.6974 25.9466L21.5025 25.7047L21.238 25.3691L20.7983 24.7694L20.4182 24.1942L20.0262 23.5589L19.8035 23.1921L19.5343 22.744L19.0808 21.958L18.4733 20.8363L18.0084 19.957L17.7401 19.4471L17.2831 18.5582L16.6542 17.3029L15.6674 15.3382L14.8182 13.6397L14.3125 12.6591L13.842 11.756L13.2296 10.6025L12.7747 9.7923L12.5052 9.32967L12.2782 8.94359L11.8796 8.2746L11.4949 7.66695L11.052 7.02917L10.7863 6.67041L10.5863 6.40559L10.2279 5.94657L9.9638 5.64195L9.77487 5.44721L9.56631 5.25257L9.33009 5.05243L9.19911 4.94853L9.0245 4.81631L8.77299 4.64471L8.60732 4.54947L8.4393 4.47062L8.26555 4.40908L8.08268 4.36578L7.8873 4.34162L7.78387 4.33702L7.6941 4.33774L7.52232 4.35397L7.35986 4.38936L7.20544 4.44325L7.05781 4.515L6.9157 4.60396L6.77786 4.70946L6.64302 4.83086L6.57632 4.89732L6.48348 4.99562L6.31919 5.18746L6.17907 5.37673L6.0579 5.56792L5.95046 5.76556L5.85151 5.97415L5.70757 6.31744L5.60699 6.57315L5.47259 6.93043L5.26504 7.5635L5.11014 8.16295L4.97309 8.81886L4.90038 9.19613L4.81478 9.65898L4.68406 10.4666L4.5903 11.2176L4.5131 12.033L4.47434 12.5027L4.44656 12.8788L4.40037 13.7006L4.3667 14.6151L4.34554 15.6223L4.33964 16.1608"
      stroke="currentColor"
      strokeWidth={props.strokeWidth || 1.5}
    />
  </svg>
);

export const Lissajous5Icon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 41 40" fill="none">
    <g clipPath="url(#clip0_859_1195)">
      <path
        d="M3.08272 39.9296C2.74114 39.9292 2.40276 39.8639 2.08559 39.7371C1.70427 39.5871 1.18344 39.2291 0.867319 38.3744C-0.22785 35.4135 1.79063 28.3262 6.01689 20.2401C0.613845 10.3472 -0.688573 2.21146 1.82517 0.401581C3.75238 -0.986852 6.93105 1.33945 13.2782 9.29585C18.0854 3.64408 23.2682 0.247329 27.5799 0.247329C31.148 0.247329 34.3716 2.05636 36.6567 5.34136C39.1207 8.88322 40.423 14.0191 40.423 20.1935C40.423 33.6522 33.4427 39.9225 28.7252 39.9225C24.5802 39.9225 19.5699 38.7288 13.1509 30.9278L12.9965 31.1156C8.07786 37.0935 5.344 39.9296 3.08272 39.9296ZM14.2063 29.6349C20.2974 37.1419 24.9243 38.2862 28.7252 38.2862C32.2337 38.2862 38.7868 32.979 38.7868 20.1932C38.7868 6.67832 32.7498 1.88297 27.5799 1.88297C23.7143 1.88297 18.8538 5.20725 14.3192 10.6144C16.2882 13.1344 18.5404 16.1512 21.1259 19.7128L21.4698 20.1869L21.1315 20.6659C21.1058 20.7018 18.7034 24.0945 14.2063 29.6352V29.6349ZM6.97897 21.9455C2.83737 30.1202 1.68006 35.8557 2.40171 37.8068C2.48349 38.027 2.58644 38.176 2.68447 38.2146C4.17027 38.7997 7.1278 35.6736 11.7332 30.0761C11.86 29.9219 11.9853 29.7696 12.1089 29.619C11.1776 28.4115 10.2181 27.0658 9.22806 25.5663C8.42006 24.3425 7.67036 23.1356 6.97897 21.9455ZM7.87857 20.2213C8.70825 21.6981 9.61716 23.1858 10.5935 24.6645C11.48 26.0073 12.3375 27.2199 13.1705 28.315C16.5329 24.1612 18.6664 21.278 19.4537 20.1957C17.1061 16.9681 15.0563 14.2302 13.2588 11.9246C11.3536 14.3621 9.5259 17.16 7.87857 20.2213ZM3.01059 1.67437C2.87157 1.67437 2.80351 1.71331 2.78116 1.72923C2.25626 2.10766 1.84278 4.32137 2.97198 8.65057C3.75679 11.6587 5.12439 15.034 6.94968 18.5123C8.58329 15.5887 10.3622 12.9263 12.2118 10.5947C10.4086 8.32768 8.8796 6.53389 7.56719 5.14257C4.69466 2.09598 3.46894 1.67437 3.01059 1.67437Z"
        fill="currentColor"
      />
      <path
        d="M37.9123 39.9296C35.651 39.9296 32.9173 37.094 27.9985 31.1156L27.8441 30.9278C21.4251 38.7288 16.4149 39.9225 12.2697 39.9225C7.55229 39.9225 0.572021 33.6522 0.572021 20.1935C0.572021 14.019 1.87444 8.88284 4.3384 5.34132C6.62424 2.05649 9.84693 0.247288 13.415 0.247288C17.7271 0.247288 22.9096 3.64404 27.7168 9.29581C34.0638 1.33992 37.2433 -0.985539 39.1698 0.40154C41.6836 2.21141 40.3811 10.3471 34.9781 20.2404C39.2044 28.3262 41.2228 35.4134 40.1277 38.3747C39.8115 39.2291 39.2907 39.5874 38.9094 39.7374C38.5922 39.8641 38.2538 39.9293 37.9123 39.9296ZM13.415 1.8831C8.24515 1.88327 2.20834 6.67862 2.20834 20.1935C2.20834 32.9793 8.76107 38.2865 12.2699 38.2865C16.0705 38.2865 20.6977 37.1422 26.7889 29.6352C22.2919 24.0945 19.8894 20.7018 19.8636 20.6654L19.526 20.1871L19.8699 19.713C22.4548 16.1506 24.7069 13.1345 26.6766 10.6145C22.1413 5.20688 17.2806 1.88327 13.415 1.88327V1.8831ZM28.8861 29.6179C29.0103 29.7692 29.1355 29.9216 29.2618 30.0751C33.8674 35.6722 36.8213 38.7989 38.3107 38.2135C38.4085 38.1749 38.5117 38.0273 38.5933 37.8058C39.3149 35.8547 38.1578 30.1186 34.016 21.9444C33.3235 23.1333 32.5738 24.3401 31.7669 25.5649C30.7769 27.0662 29.817 28.4112 28.8861 29.6186V29.6179ZM21.5413 20.195C22.3281 21.2773 24.4622 24.1605 27.8244 28.3143C28.6567 27.2191 29.5149 26.0066 30.4015 24.6637C31.3778 23.1851 32.2861 21.6974 33.1164 20.2206C31.4691 17.1593 29.6414 14.3621 27.7362 11.9238C25.9387 14.2302 23.8889 16.9681 21.5413 20.1957V20.195ZM28.7831 10.5945C30.6328 12.9261 32.4117 15.5885 34.0455 18.5121C35.8706 15.0334 37.2382 11.6585 38.023 8.65036C39.1522 4.32116 38.7394 2.10745 38.2138 1.72902C38.1182 1.65977 37.1725 1.17026 33.428 5.14236C32.1159 6.53385 30.5866 8.32764 28.7831 10.5947V10.5945Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_859_1195">
        <rect width="40" height="40" fill="white" transform="translate(0.5)" />
      </clipPath>
    </defs>
  </svg>
);

export const CommandIcon = (props: React.ComponentProps<"svg">, strokeWidth: number = 2): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24" fill="none">
    <path
      d="M23 19.3333C23 21.3586 21.3586 23 19.3333 23C17.3081 23 15.6667 21.3586 15.6667 19.3333V4.66667C15.6667 2.64144 17.3081 1 19.3333 1C21.3586 1 23 2.64144 23 4.66667C23 6.69189 21.3586 8.33333 19.3333 8.33333H4.66667C2.64144 8.33333 1 6.69189 1 4.66667C1 2.64144 2.64144 1 4.66667 1C6.69189 1 8.33333 2.64144 8.33333 4.66667V19.3333C8.33333 21.3586 6.69189 23 4.66667 23C2.64144 23 1 21.3586 1 19.3333C1 17.3081 2.64144 15.6667 4.66667 15.6667H19.3333C21.3586 15.6667 23 17.3081 23 19.3333Z"
      stroke="currentColor"
      strokeMiterlimit={10}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ReturnIcon = (props: React.ComponentProps<"svg">, strokeWidth: number = 2): JSX.Element => (
  <svg {...props} viewBox="0 0 24 25" fill="none">
    <path
      d="M5.25 24.247L0.75 19.747M0.75 19.747L5.25 15.247M0.75 19.747H18.75C21.2353 19.747 23.25 17.7323 23.25 15.247V6.24698C23.25 3.7617 21.2353 1.74698 18.75 1.74698H10.5"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SmallUpTriangle = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="7" height="6" viewBox="0 0 7 6">
    <path d="M3.5 0.5L7 5.5H0L3.5 0.5Z" fill="currentColor" />
  </svg>
);

export const SmallDownTriangle = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="7" height="6" viewBox="0 0 7 6" fill="none">
    <path d="M3.5 5.5L7 0.5H0L3.5 5.5Z" fill="currentColor" />
  </svg>
);

export const TwitterIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M24 6.32327L21.745 5.35683L22.9262 2.77965L20.1772 3.6924C19.3016 2.87622 18.1526 2.41663 16.9557 2.40381C14.2894 2.40971 12.1294 4.56971 12.1235 7.23602V8.30985C8.32215 9.09374 5.00403 7.02126 1.92215 3.47763C1.38523 6.34117 1.92215 8.48882 3.53289 9.92059L0 9.38367C0.28169 11.731 2.20365 13.5399 4.56376 13.679L1.61074 14.7528C2.68456 16.9005 4.63893 17.2333 7.24832 17.4374C5.11551 18.8921 2.58102 19.643 0 19.585C13.702 25.6736 21.745 16.7286 21.745 8.84676V7.95549L24 6.32327Z"
      fill="currentColor"
    />
  </svg>
);

export const SlackIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="-20 0 160 127" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
      fill="#E01E5A"
    />
    <path
      d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z"
      fill="#36C5F0"
    />
    <path
      d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
      fill="#2EB67D"
    />
    <path
      d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
      fill="#ECB22E"
    />
  </svg>
);

export const SlackMonochromeIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="-20 0 160 127" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
      fill="currentColor"
    />
    <path
      d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z"
      fill="currentColor"
    />
    <path
      d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
      fill="currentColor"
    />
    <path
      d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
      fill="currentColor"
    />
  </svg>
);

export const KeyboardRightArrowIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M23.25 12.4961H0.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.5 16.2461L23.25 12.4961L19.5 8.74609"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InsightIcon = (): JSX.Element => {
  return (
    <svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 15.75H14.25C13.4216 15.75 12.75 15.0784 12.75 14.25V10.5C12.75 10.0858 12.4142 9.74999 12 9.74999H11.25M12.75 0.749988C8.93933 0.744494 5.42568 2.80692 3.5728 6.1368C1.71991 9.46667 1.81953 13.5397 3.833 16.775L0.75 23.25L7.224 20.166C11.0101 22.5209 15.8706 22.2279 19.3464 19.4351C22.8222 16.6424 24.1551 11.959 22.6708 7.75458C21.1865 3.55015 17.2087 0.741629 12.75 0.749988ZM12.75 6.37499C12.75 6.16788 12.5821 5.99999 12.375 5.99999C12.1679 5.99999 12 6.16788 12 6.37499C12 6.58209 12.1679 6.74999 12.375 6.74999C12.5821 6.74999 12.75 6.58209 12.75 6.37499Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const LoadingAltIcon = ({ className }: { className?: string }): JSX.Element => {
  return (
    <svg
      className={classNames(className, "animate-spin")}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export const DatabaseIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-0.125 -0.125 14.25 14.25"
      height={14}
      width={14}
      strokeWidth={1.25}
      {...props}
    >
      <g>
        <ellipse
          cx={7}
          cy={3}
          rx={6.5}
          ry={2.5}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M.5,3v8c0,1.38,2.91,2.5,6.5,2.5s6.5-1.12,6.5-2.5V3"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.5,7c0,1.38-2.91,2.5-6.5,2.5S.5,8.38.5,7"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

export const NumberIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M20.25 24.246H2.25C1.42157 24.246 0.75 23.5745 0.75 22.746V4.74603M14.25 15.246V8.50003C14.2492 8.21629 14.0886 7.9572 13.8349 7.83031C13.5811 7.70342 13.2774 7.73039 13.05 7.90003L11.25 9.25003M12.75 15.246H15.75M6 21.246H21C22.2426 21.246 23.25 20.2387 23.25 18.996V3.99603C23.25 2.75339 22.2426 1.74603 21 1.74603H6C4.75736 1.74603 3.75 2.75339 3.75 3.99603V18.996C3.75 20.2387 4.75736 21.246 6 21.246Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ShapeCubeIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  // Necker cube
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.629 23.25C17.0265 23.2499 17.4078 23.092 17.689 22.811L22.811 17.689C23.092 17.4078 23.2499 17.0265 23.25 16.629V2.25C23.25 1.42157 22.5784 0.75 21.75 0.75H7.371C6.97345 0.750085 6.5922 0.907982 6.311 1.189L1.189 6.311C0.907982 6.5922 0.750085 6.97345 0.75 7.371V21.75C0.75 22.5784 1.42157 23.25 2.25 23.25H16.629Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.25 23.115V6.75"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 17.25H23.115"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.18945 22.811L6.75045 17.25"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.25 6.74996L22.811 1.18896"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.2498 6.75H0.884766"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 0.88501V17.25"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckCircleIcon = (
  // Slightly more sparse than heroicons
  props: React.ComponentProps<"svg">,
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M8.5 13L11.5 15L15.5 9M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckCircleSolidIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <path
      d="M20 3.75C16.7861 3.75 13.6443 4.70305 10.972 6.48862C8.29969 8.27419 6.21689 10.8121 4.98696 13.7814C3.75704 16.7507 3.43524 20.018 4.06225 23.1702C4.68926 26.3224 6.23692 29.2179 8.50952 31.4905C10.7821 33.7631 13.6776 35.3107 16.8298 35.9378C19.982 36.5648 23.2493 36.243 26.2186 35.013C29.1879 33.7831 31.7258 31.7003 33.5114 29.028C35.297 26.3557 36.25 23.2139 36.25 20C36.2455 15.6916 34.5319 11.561 31.4855 8.51454C28.439 5.46806 24.3084 3.75455 20 3.75ZM27.1344 17.1344L18.3844 25.8844C18.2683 26.0006 18.1304 26.0928 17.9787 26.1557C17.8269 26.2186 17.6643 26.251 17.5 26.251C17.3357 26.251 17.1731 26.2186 17.0213 26.1557C16.8696 26.0928 16.7317 26.0006 16.6156 25.8844L12.8656 22.1344C12.6311 21.8998 12.4993 21.5817 12.4993 21.25C12.4993 20.9183 12.6311 20.6002 12.8656 20.3656C13.1002 20.1311 13.4183 19.9993 13.75 19.9993C14.0817 19.9993 14.3998 20.1311 14.6344 20.3656L17.5 23.2328L25.3656 15.3656C25.4818 15.2495 25.6196 15.1574 25.7714 15.0945C25.9231 15.0317 26.0858 14.9993 26.25 14.9993C26.4143 14.9993 26.5769 15.0317 26.7286 15.0945C26.8804 15.1574 27.0182 15.2495 27.1344 15.3656C27.2505 15.4818 27.3426 15.6196 27.4055 15.7714C27.4684 15.9231 27.5007 16.0858 27.5007 16.25C27.5007 16.4142 27.4684 16.5769 27.4055 16.7286C27.3426 16.8804 27.2505 17.0182 27.1344 17.1344Z"
      fill="currentColor"
    />
  </svg>
);

export const ArrowUpRightIcon = (
  // Slightly more sparse than heroicons
  props: React.ComponentProps<"svg">,
): JSX.Element => (
  <svg
    {...props}
    width="12"
    height="12"
    viewBox="0 -1 13 13" // edited to make a bit smaller
    fill="none"
  >
    <path
      d="M2.5 9.5L9.5 2.5M9.5 2.5H2.90038M9.5 2.5V9.09962"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FeedbackIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="17" height="16" viewBox="0 0 17 16" fill="none">
    <path d="M1.48047 6V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.8809 1V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M1.48047 7L15.8805 2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.48047 9L15.8805 14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.28125 9.97266V9.99999C4.33718 11.8788 5.51478 13.5208 7.22856 14.1096C8.94234 14.6983 10.8266 14.1082 11.9446 12.6327"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CodeFileIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18.045 0.753052L18.0451 0.00305176H18.045V0.753052ZM19.093 1.18005L18.5689 1.71655L18.5693 1.71697L19.093 1.18005ZM22.048 4.06205L22.572 3.52547L22.5717 3.52513L22.048 4.06205ZM22.5 5.13605L21.75 5.13564V5.13605H22.5ZM14.8253 8.47272C14.5324 8.17983 14.0576 8.17983 13.7647 8.47272C13.4718 8.76561 13.4718 9.24049 13.7647 9.53338L14.8253 8.47272ZM18.045 12.7531L18.5753 13.2834C18.8682 12.9905 18.8682 12.5156 18.5753 12.2227L18.045 12.7531ZM13.7647 15.9727C13.4718 16.2656 13.4718 16.7405 13.7647 17.0334C14.0576 17.3263 14.5324 17.3263 14.8253 17.0334L13.7647 15.9727ZM10.3253 9.53338C10.6182 9.24049 10.6182 8.76561 10.3253 8.47272C10.0324 8.17983 9.55756 8.17983 9.26467 8.47272L10.3253 9.53338ZM6.045 12.7531L5.51467 12.2227C5.22178 12.5156 5.22178 12.9905 5.51467 13.2834L6.045 12.7531ZM9.26467 17.0334C9.55756 17.3263 10.0324 17.3263 10.3253 17.0334C10.6182 16.7405 10.6182 16.2656 10.3253 15.9727L9.26467 17.0334ZM21.75 21.7531C21.75 22.1673 21.4142 22.5031 21 22.5031V24.0031C22.2426 24.0031 23.25 22.9957 23.25 21.7531H21.75ZM21 22.5031H3V24.0031H21V22.5031ZM3 22.5031C2.58579 22.5031 2.25 22.1673 2.25 21.7531H0.75C0.75 22.9957 1.75736 24.0031 3 24.0031V22.5031ZM2.25 21.7531V2.25305H0.75V21.7531H2.25ZM2.25 2.25305C2.25 1.83884 2.58579 1.50305 3 1.50305V0.00305176C1.75736 0.00305176 0.75 1.01041 0.75 2.25305H2.25ZM3 1.50305H18.045V0.00305176H3V1.50305ZM18.0449 1.50305C18.2408 1.50307 18.4288 1.5797 18.5689 1.71655L19.6171 0.643552C19.1968 0.232997 18.6326 0.00312036 18.0451 0.00305176L18.0449 1.50305ZM18.5693 1.71697L21.5243 4.59897L22.5717 3.52513L19.6167 0.643131L18.5693 1.71697ZM21.524 4.59864C21.6686 4.73987 21.7501 4.9335 21.75 5.13564L23.25 5.13647C23.2503 4.53004 23.0059 3.94916 22.572 3.52547L21.524 4.59864ZM21.75 5.13605V21.7531H23.25V5.13605H21.75ZM13.7647 9.53338L17.5147 13.2834L18.5753 12.2227L14.8253 8.47272L13.7647 9.53338ZM17.5147 12.2227L13.7647 15.9727L14.8253 17.0334L18.5753 13.2834L17.5147 12.2227ZM9.26467 8.47272L5.51467 12.2227L6.57533 13.2834L10.3253 9.53338L9.26467 8.47272ZM5.51467 13.2834L9.26467 17.0334L10.3253 15.9727L6.57533 12.2227L5.51467 13.2834Z"
      fill="currentColor"
    />
  </svg>
);

export const FlaskIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M6.726 0.75H17.226M5.301 12.75H18.651M14.226 17.25H17.226M15.726 15.75V18.75M15.726 3.75H12.726M15.726 6.75H12.726M15.726 8.25V0.75H8.226V8.25L1.489 18.615C0.889844 19.5367 0.842849 20.7124 1.36652 21.679C1.89019 22.6456 2.90063 23.2484 4 23.25H19.948C21.0481 23.2499 22.0599 22.6477 22.5845 21.6808C23.1092 20.7139 23.0625 19.5373 22.463 18.615L15.726 8.25ZM6.726 19.125C6.93272 19.1256 7.1 19.2933 7.1 19.5C7.1 19.7071 6.93311 19.875 6.726 19.875C6.51889 19.875 6.351 19.7071 6.351 19.5C6.351 19.2929 6.51889 19.125 6.726 19.125ZM9.726 16.125C9.93311 16.125 10.101 16.2929 10.101 16.5C10.101 16.7071 9.93311 16.875 9.726 16.875C9.51889 16.875 9.351 16.7071 9.351 16.5C9.351 16.2929 9.51889 16.125 9.726 16.125Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MoneyGraphArrowIncreaseIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 14 14">
    <g>
      <polyline
        points="9.5 3.5 13.5 3.5 13.5 7.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></polyline>
      <path
        d="M13.5,3.5,7.85,9.15a.5.5,0,0,1-.7,0l-2.3-2.3a.5.5,0,0,0-.7,0L.5,10.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const BrainHeadIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19.2494 23.25V16.985C22.0773 14.3652 23.011 10.2802 21.602 6.69208C20.1931 3.10391 16.7293 0.745664 12.8744 0.750006C5.73544 0.750006 4.19944 6.62801 1.64944 13.23C1.56093 13.4607 1.59158 13.7202 1.7314 13.924C1.87123 14.1277 2.10233 14.2496 2.34944 14.25H4.24944V17.25C4.24944 18.9069 5.59259 20.25 7.24944 20.25H8.74944V23.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.31701 7.70003C9.12158 7.59762 8.98099 7.41454 8.93247 7.19931C8.88396 6.98407 8.93241 6.75838 9.06501 6.58203C10.6137 4.5387 13.4116 3.91257 15.6835 5.10089C17.9554 6.2892 19.037 8.94448 18.242 11.382C18.1736 11.5919 18.0162 11.761 17.8118 11.8442C17.6073 11.9275 17.3766 11.9164 17.181 11.814L9.31701 7.70003Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DollarIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.5 4.5C16.9142 4.5 17.25 4.16421 17.25 3.75C17.25 3.33579 16.9142 3 16.5 3V4.5ZM10.8 3.75V4.5V3.75ZM7.5 19.5C7.08579 19.5 6.75 19.8358 6.75 20.25C6.75 20.6642 7.08579 21 7.5 21V19.5ZM11.25 3.75C11.25 4.16421 11.5858 4.5 12 4.5C12.4142 4.5 12.75 4.16421 12.75 3.75H11.25ZM12.75 0.75C12.75 0.335786 12.4142 0 12 0C11.5858 0 11.25 0.335786 11.25 0.75H12.75ZM12.75 20.25C12.75 19.8358 12.4142 19.5 12 19.5C11.5858 19.5 11.25 19.8358 11.25 20.25H12.75ZM11.25 23.25C11.25 23.6642 11.5858 24 12 24C12.4142 24 12.75 23.6642 12.75 23.25H11.25ZM16.5 3H10.8V4.5H16.5V3ZM10.8 3C8.56325 3 6.75 4.81325 6.75 7.05H8.25C8.25 5.64167 9.39167 4.5 10.8 4.5V3ZM6.75 7.05C6.75 8.55543 7.4491 9.67077 8.39946 10.5419C9.32249 11.388 10.534 12.0496 11.6386 12.6572C12.784 13.2871 13.8225 13.863 14.587 14.5638C15.3241 15.2395 15.75 15.9804 15.75 16.95H17.25C17.25 15.4446 16.5509 14.3292 15.6005 13.4581C14.6775 12.612 13.466 11.9504 12.3614 11.3428C11.216 10.7129 10.1775 10.137 9.41304 9.4362C8.6759 8.76048 8.25 8.01957 8.25 7.05H6.75ZM15.75 16.95C15.75 18.3583 14.6083 19.5 13.2 19.5V21C15.4368 21 17.25 19.1868 17.25 16.95H15.75ZM13.2 19.5H7.5V21H13.2V19.5ZM12.75 3.75V0.75H11.25V3.75H12.75ZM11.25 20.25V23.25H12.75V20.25H11.25Z"
      fill="currentColor"
    />
  </svg>
);

export const ClockIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 12H11.25C11.25 12.1989 11.329 12.3896 11.4696 12.5303L12 12ZM12.75 8.25C12.75 7.83579 12.4142 7.5 12 7.5C11.5858 7.5 11.25 7.83579 11.25 8.25H12.75ZM16.1566 17.2183C16.4495 17.5112 16.9243 17.5112 17.2173 17.2184C17.5102 16.9255 17.5102 16.4507 17.2174 16.1577L16.1566 17.2183ZM12 23.25C18.2132 23.25 23.25 18.2132 23.25 12H21.75C21.75 17.3848 17.3848 21.75 12 21.75V23.25ZM23.25 12C23.25 5.7868 18.2132 0.75 12 0.75V2.25C17.3848 2.25 21.75 6.61522 21.75 12H23.25ZM12 0.75C5.7868 0.75 0.75 5.7868 0.75 12H2.25C2.25 6.61522 6.61522 2.25 12 2.25V0.75ZM0.75 12C0.75 18.2132 5.7868 23.25 12 23.25V21.75C6.61522 21.75 2.25 17.3848 2.25 12H0.75ZM12.75 12V8.25H11.25V12H12.75ZM11.4696 12.5303L16.1566 17.2183L17.2174 16.1577L12.5304 11.4697L11.4696 12.5303Z"
      fill="currentColor"
    />
  </svg>
);

export const PencilIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.0938 3.34251L20.6572 7.906M3.57417 15.8614L8.14203 20.4214M21.0572 2.94252C20.4456 2.33354 19.616 1.99423 18.753 2.00007C17.89 2.00592 17.0651 2.35643 16.4618 2.97363L3.57329 15.8623L2 22L8.13759 20.4258L21.0261 7.53711C21.6434 6.9341 21.9941 6.10928 21.9999 5.2463C22.0058 4.38333 21.6663 3.55383 21.0572 2.94252Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RefreshIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M5.25 14.248V18.748H0.75M18.75 9.74796V5.24796H23.25M19.032 5.24496C22.2304 8.57707 22.6526 13.6959 20.0434 17.507C17.4343 21.3181 12.5095 22.7763 8.246 21M4.967 18.751C1.77575 15.4174 1.35778 10.3033 3.96537 6.49579C6.57296 2.68823 11.4923 1.22944 15.754 2.99996"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LightningIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="14" height="14" viewBox="0 0 14 14" fill="none">
    <polygon
      points="8 0.5 8 5.5 11.5 5.5 6 13.5 6 8.5 2.5 8.5 8 0.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></polygon>
  </svg>
);

export const ResetIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-0.125 -0.125 14.25 14.25"
    height={24}
    width={24}
    strokeWidth={1.25}
    {...props}
  >
    <g>
      <polyline
        points="4.75 0.5 2.25 3 4.75 5.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.75,8.25A5.25,5.25,0,1,0,7,3H2.25"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
export const UsersIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="-0.125 -0.125 14.25 14.25" height={24} width={24} strokeWidth={1.25} {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 6.5C6.38071 6.5 7.5 5.38071 7.5 4C7.5 2.61929 6.38071 1.5 5 1.5C3.61929 1.5 2.5 2.61929 2.5 4C2.5 5.38071 3.61929 6.5 5 6.5Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M0.5 13.5H5H9.5V12.9579C9.49204 12.1956 9.29102 11.4479 8.91573 10.7844C8.54043 10.121 8.0031 9.56347 7.35393 9.16398C6.70475 8.76448 5.96491 8.53605 5.20352 8.5C5.13564 8.49679 5.06778 8.49511 5 8.49496C4.93222 8.49511 4.86436 8.49679 4.79648 8.5C4.03509 8.53605 3.29525 8.76448 2.64607 9.16398C1.9969 9.56347 1.45957 10.121 1.08427 10.7844C0.708977 11.4479 0.507961 12.1956 0.5 12.9579V13.5Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 6.5C10.3807 6.5 11.5 5.38071 11.5 4C11.5 2.61929 10.3807 1.5 9 1.5"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.5 13.5H13.5V12.9579C13.492 12.1956 13.291 11.4479 12.9157 10.7844C12.5404 10.121 12.0031 9.56347 11.3539 9.16398C10.9343 8.90573 10.4767 8.71897 10 8.60945"
    />
  </svg>
);

export const SeesawIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="-0.125 -0.125 14.25 14.25" height={24} width={24} strokeWidth={1.25} {...props}>
    <g>
      <line
        x1={0.5}
        y1={8.9}
        x2={13.5}
        y2={3.9}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="4.5 10.9 9.5 10.9 7 6.4 4.5 10.9"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={3.27}
        y1={7.84}
        x2={2.55}
        y2={5.97}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={10.73}
        y1={4.97}
        x2={10.02}
        y2={3.1}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const DiamondIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" {...props}>
    <path
      d="M3 13L12 15.021M12 15.021L21 13M12 15.021V24.25M12 15.021V8V1.75296M11.31 2.06996L3.331 12.07C2.88991 12.61 2.88991 13.3859 3.331 13.926L11.31 23.926C11.481 24.1302 11.7337 24.2481 12 24.2481C12.2663 24.2481 12.519 24.1302 12.69 23.926L20.669 13.926C21.1101 13.3859 21.1101 12.61 20.669 12.07L12.69 2.06996C12.519 1.86577 12.2663 1.7478 12 1.7478C11.7337 1.7478 11.481 1.86577 11.31 2.06996Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ExpandIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M10 14L3 21M3 21V16M3 21H8M21 8V3M21 3H16M21 3L14 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
export const ContractIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M21 3L14 10M14 10V5M14 10H19M10 19V14M10 14H5M10 14L3 21"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const VariableXIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M4.74499 3C3.58881 5.85953 2.99627 8.91558 2.99999 12C2.99999 15.183 3.61999 18.22 4.74499 21M19.5 3C20.467 5.78 21 8.817 21 12C21 15.183 20.467 18.22 19.5 21M8.24999 8.885L9.69399 7.995C9.79028 7.93569 9.89875 7.89893 10.0113 7.88747C10.1238 7.876 10.2374 7.89013 10.3437 7.9288C10.45 7.96746 10.5461 8.02966 10.625 8.11074C10.7038 8.19182 10.7633 8.28968 10.799 8.397L13.201 15.603C13.2368 15.7101 13.2964 15.8077 13.3752 15.8886C13.454 15.9694 13.5501 16.0314 13.6562 16.07C13.7623 16.1085 13.8758 16.1226 13.9881 16.1112C14.1005 16.0998 14.2088 16.0631 14.305 16.004L15.75 15.115M7.49999 15.865L7.71299 15.955C8.07475 16.1084 8.47871 16.1307 8.85517 16.0181C9.23162 15.9054 9.55694 15.6649 9.77499 15.338L14.225 8.662C14.443 8.33508 14.7683 8.09448 15.1447 7.98167C15.5211 7.86885 15.9251 7.8909 16.287 8.044L16.5 8.134"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    s
  </svg>
);

export const VariableYIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M4.74499 3C3.58881 5.85953 2.99627 8.91558 2.99999 12C2.99999 15.183 3.61999 18.22 4.74499 21M19.5 3C20.467 5.78 21 8.817 21 12C21 15.183 20.467 18.22 19.5 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.75 11.2204L10.194 10.1358C10.2903 10.0635 10.3988 10.0187 10.5113 10.0047C10.6238 9.99074 10.7374 10.008 10.8437 10.0551C10.95 10.1022 11.0461 10.178 11.125 10.2768C11.2038 10.3756 11.2633 10.4949 11.299 10.6257L12 15.9309M8 19.7272L8.213 19.8369C8.57476 20.0238 8.97873 20.051 9.35518 19.9137C9.73163 19.7764 10.0569 19.4833 10.275 19.0849L13.1375 14.5448L16 10.0047"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RocketIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-0.125 -0.125 14.25 14.25"
    height={24}
    width={24}
    strokeWidth={1.25}
    {...props}
  >
    <g>
      <path
        d="M5.71,4.31C3.79,3,2,4,.5,5.54l3.32,2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.69,8.29C11,10.21,10,12,8.46,13.5l-2-3.32"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.82,7.53l2.65,2.65C8.59,8.91,11,7.68,12.1,6.54c2.38-2.38,1-5.64,1-5.64S9.84-.48,7.46,1.9C6.32,3,5.08,5.42,3.82,7.53Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={5.28}
        y1={5.02}
        x2={8.98}
        y2={8.72}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={10.5}
        cy={3.5}
        r={0.5}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.68,12.44C3.1,13,.5,13.5.5,13.5S1,10.9,1.56,10.32a1.5,1.5,0,1,1,2.12,2.12Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const SparklesIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <g clipPath="url(#clip0_3362_1925)">
      <path
        d="M18 11.9813C18 11.7 17.8418 11.4366 17.584 11.3072L12.3105 8.66766L9.67031 3.38391C9.41836 2.87391 8.58656 2.87391 8.33438 3.38391L5.69063 8.66719L0.416016 11.3062C0.158203 11.3953 0 11.7 0 11.9813C0 12.2684 0.158203 12.5264 0.416016 12.6553L5.68945 15.2977L8.33227 20.5805C8.45625 20.8406 8.67656 20.9953 9 20.9953C9.23906 20.9953 9.54469 20.8365 9.66797 20.5787L12.3108 15.2959L17.5842 12.6536C17.8406 12.5297 18 12.2719 18 11.9813ZM11.4141 14.0672C11.2676 14.1375 11.1504 14.2547 11.0801 14.4012L9 18.5625L6.91875 14.4047C6.84844 14.2582 6.73125 14.141 6.58477 14.0707L2.42461 11.9848L6.58594 9.90469C6.73125 9.825 6.84844 9.70781 6.91875 9.5625L9 5.40469L11.0812 9.5625C11.1516 9.70898 11.2687 9.82617 11.4152 9.90234L15.5754 11.9827L11.4141 14.0672ZM15.75 5.25H18.75V8.25C18.75 8.6625 19.0875 9 19.5 9C19.9125 9 20.25 8.66456 20.25 8.25V5.25H23.25C23.6625 5.25 24 4.9125 24 4.5C24 4.0875 23.6646 3.75 23.25 3.75H20.25V0.75C20.25 0.335438 19.9125 0 19.5 0C19.0875 0 18.75 0.335438 18.75 0.75V3.75H15.75C15.3375 3.75 15 4.08563 15 4.5C15 4.91437 15.3375 5.25 15.75 5.25ZM23.25 18.75H20.25V15.75C20.25 15.3354 19.9146 15 19.5 15C19.0854 15 18.75 15.3354 18.75 15.75V18.75H15.75C15.3354 18.75 15 19.0854 15 19.5C15 19.9146 15.3354 20.25 15.75 20.25H18.75V23.25C18.75 23.6646 19.0854 24 19.5 24C19.9146 24 20.25 23.6646 20.25 23.25V20.25H23.25C23.6646 20.25 24 19.9146 24 19.5C24 19.0854 23.6625 18.75 23.25 18.75Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_3362_1925">
        <rect width="24" height="24" fill="transparent" />
      </clipPath>
    </defs>
  </svg>
);

export const ThumbsUpIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M2 9.00001V21M20 9.00001H12.356L13.828 5.11101C14.205 4.11501 13.87 2.97601 13.026 2.38101C12.062 1.70201 10.762 1.95401 10.089 2.95001L6 9.00001V18C6 20.209 7.791 22 10 22H16.516C18.028 22 19.41 21.148 20.089 19.798L22 16V11C22 9.89501 21.105 9.00001 20 9.00001Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ThumbsDownIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M22 15V3M4 15H11.644L10.172 18.889C9.795 19.885 10.13 21.024 10.974 21.619C11.938 22.298 13.238 22.046 13.911 21.05L18 15V6C18 3.791 16.209 2 14 2H7.484C5.972 2 4.59 2.852 3.911 4.202L2 8V13C2 14.105 2.895 15 4 15Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ThumbsUpSolidIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M20 9.00001H12.375L13.847 5.11101C14.224 4.11501 13.889 2.97601 13.045 2.38101C12.081 1.70201 10.781 1.95401 10.108 2.95001L6.019 9.00001H6V18C6 20.209 7.791 22 10 22H16.7C17.49 22 18.207 21.534 18.528 20.812L21.828 13.387C21.941 13.132 22 12.855 22 12.575V11C22 9.89501 21.105 9.00001 20 9.00001ZM3 22C2.448 22 2 21.552 2 21V10C2 9.44801 2.448 9.00001 3 9.00001C3.552 9.00001 4 9.44801 4 10V21C4 21.552 3.552 22 3 22Z"
      fill="currentColor"
    />
  </svg>
);

export const ThumbsDownSolidIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M4 15H11.625L10.153 18.889C9.776 19.885 10.111 21.024 10.955 21.619C11.919 22.298 13.219 22.046 13.892 21.05L17.981 15H18V6C18 3.791 16.209 2 14 2H7.3C6.51 2 5.793 2.466 5.472 3.188L2.172 10.613C2.059 10.868 2 11.145 2 11.425V13C2 14.105 2.895 15 4 15ZM21 2C21.552 2 22 2.448 22 3V14C22 14.552 21.552 15 21 15C20.448 15 20 14.552 20 14V3C20 2.448 20.448 2 21 2Z"
      fill="currentColor"
    />
  </svg>
);

export const OffensiveIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12.875 7.375C12.875 7.07833 12.963 6.78832 13.1278 6.54165C13.2926 6.29497 13.5269 6.10271 13.801 5.98918C14.0751 5.87565 14.3767 5.84594 14.6676 5.90382C14.9586 5.9617 15.2259 6.10456 15.4357 6.31434C15.6454 6.52412 15.7883 6.79139 15.8462 7.08237C15.9041 7.37334 15.8744 7.67494 15.7608 7.94903C15.6473 8.22312 15.455 8.45738 15.2084 8.62221C14.9617 8.78703 14.6717 8.875 14.375 8.875V9.9"
    />
    <path
      stroke="currentColor"
      d="M14.25 12.625C14.0429 12.625 13.875 12.4571 13.875 12.25C13.875 12.0429 14.0429 11.875 14.25 11.875"
    />
    <path
      stroke="currentColor"
      d="M14.25 12.625C14.4571 12.625 14.625 12.4571 14.625 12.25C14.625 12.0429 14.4571 11.875 14.25 11.875"
    />
    <path
      stroke="currentColor"
      d="M19.125 12.625C18.9179 12.625 18.75 12.4571 18.75 12.25C18.75 12.0429 18.9179 11.875 19.125 11.875"
    />
    <path
      stroke="currentColor"
      d="M19.125 12.625C19.3321 12.625 19.5 12.4571 19.5 12.25C19.5 12.0429 19.3321 11.875 19.125 11.875"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 0.75C1.85218 0.75 1.47064 0.908035 1.18934 1.18934C0.908035 1.47064 0.75 1.85218 0.75 2.25V17.25C0.75 17.6478 0.908035 18.0294 1.18934 18.3107C1.47064 18.592 1.85218 18.75 2.25 18.75H5.25V23.25L11.25 18.75H21.75C22.1478 18.75 22.5294 18.592 22.8107 18.3107C23.092 18.0294 23.25 17.6478 23.25 17.25V2.25C23.25 1.85218 23.092 1.47064 22.8107 1.18934C22.5294 0.908035 22.1478 0.75 21.75 0.75H2.25Z"
    />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6 6.5L5 12.5" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M9 6.5L8 12.5" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M4.5 8H10" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M4 11H9.5" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M19.25 5.875V9.375" />
  </svg>
);

export const ConfusedBrainIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.1999 8.00003C15.1995 8.98338 14.4382 9.79876 13.4572 9.86669C14.0287 10.601 14.1083 11.6049 13.6596 12.42C13.2108 13.2351 12.32 13.7049 11.3939 13.6147C11.5229 14.077 11.3756 14.5723 11.015 14.8891C10.6543 15.2058 10.1442 15.2879 9.70236 15.1004C9.26055 14.9128 8.96524 14.4888 8.94258 14.0094C8.36094 14.3453 7.64421 14.3453 7.06258 14.0094C7.03991 14.4888 6.74461 14.9128 6.3028 15.1004C5.861 15.2879 5.35081 15.2058 4.99019 14.8891C4.62958 14.5723 4.48229 14.077 4.61124 13.6147C3.6847 13.7061 2.79277 13.2368 2.34331 12.4214C1.89386 11.606 1.97327 10.6013 2.54524 9.86669C1.55449 9.81368 0.77832 8.99487 0.77832 8.00269C0.77832 7.01052 1.55449 6.19171 2.54524 6.13869C1.97315 5.40396 1.89377 4.39906 2.34341 3.58362C2.79305 2.76818 3.68525 2.299 4.61191 2.39069C4.47702 1.92701 4.62144 1.4271 4.98282 1.10678C5.3442 0.786465 5.85782 0.703099 6.30196 0.892674C6.74611 1.08225 7.04122 1.51081 7.05991 1.99336C7.64155 1.65739 8.35827 1.65739 8.93991 1.99336C8.96257 1.51393 9.25788 1.08988 9.69969 0.902338C10.1415 0.714799 10.6517 0.796939 11.0123 1.11367C11.3729 1.4304 11.5202 1.92572 11.3912 2.38803C12.3163 2.29885 13.2059 2.76828 13.6543 3.5823C14.1028 4.39632 14.0243 5.39906 13.4546 6.13336C14.4366 6.19996 15.1994 7.01572 15.1999 8.00003Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 9.0647V9.6907" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M8 6.18536V6.81136"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 3.30603V3.93203"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 11.944V12.57" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.34003 6.498C5.85845 6.498 6.2787 6.07774 6.2787 5.55933C6.2787 5.04092 5.85845 4.62067 5.34003 4.62067C4.82162 4.62067 4.40137 5.04092 4.40137 5.55933C4.40137 6.07774 4.82162 6.498 5.34003 6.498Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.8159 6.43538C11.3343 6.43538 11.7545 6.01512 11.7545 5.49671C11.7545 4.9783 11.3343 4.55804 10.8159 4.55804C10.2975 4.55804 9.8772 4.9783 9.8772 5.49671C9.8772 6.01512 10.2975 6.43538 10.8159 6.43538Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.87055 12.068C5.38896 12.068 5.80922 11.6478 5.80922 11.1293C5.80922 10.6109 5.38896 10.1907 4.87055 10.1907C4.35214 10.1907 3.93188 10.6109 3.93188 11.1293C3.93188 11.6478 4.35214 12.068 4.87055 12.068Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.8945 10.8161C11.4129 10.8161 11.8331 10.3958 11.8331 9.87739C11.8331 9.35898 11.4129 8.93872 10.8945 8.93872C10.3761 8.93872 9.95581 9.35898 9.95581 9.87739C9.95581 10.3958 10.3761 10.8161 10.8945 10.8161Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.08862 7.75005H4.71396C4.88004 7.75022 5.03936 7.68433 5.1568 7.56689C5.27424 7.44946 5.34013 7.29013 5.33996 7.12405V6.49805"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const QuoteMarksIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="0 0 14 14" {...props}>
    <g>
      <line
        x1="0.5"
        y1="4"
        x2="1"
        y2="7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="4"
        x2="3.5"
        y2="7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="11.25"
        y1="7"
        x2="10.75"
        y2="10"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="13.5"
        y1="7"
        x2="13"
        y2="10"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const NeutralFaceIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="-0.125 -0.125 14.25 14.25" height={16} width={16} strokeWidth={1.25} {...props}>
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M4.5 9.5H9.5" />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.8 5.45C4.66193 5.45 4.55 5.33807 4.55 5.2C4.55 5.06193 4.66193 4.95 4.8 4.95"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.8 5.45C4.93807 5.45 5.05 5.33807 5.05 5.2C5.05 5.06193 4.93807 4.95 4.8 4.95"
    />
    <g>
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.2 5.45C9.06193 5.45 8.95 5.33807 8.95 5.2C8.95 5.06193 9.06193 4.95 9.2 4.95"
      />
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.2 5.45C9.33807 5.45 9.45 5.33807 9.45 5.2C9.45 5.06193 9.33807 4.95 9.2 4.95"
      />
    </g>
  </svg>
);

export const HappyFaceIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="0 0 14 14" strokeWidth={1.25} {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.7 8C4.2 9.8 6.2 10.9 8 10.4C9.1 10 10 9.1 10.3 8"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.8 5.45C4.66193 5.45 4.55 5.33807 4.55 5.2C4.55 5.06193 4.66193 4.95 4.8 4.95"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.8 5.45C4.93807 5.45 5.05 5.33807 5.05 5.2C5.05 5.06193 4.93807 4.95 4.8 4.95"
    />
    <g>
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.2 5.45C9.06193 5.45 8.95 5.33807 8.95 5.2C8.95 5.06193 9.06193 4.95 9.2 4.95"
      />
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.2 5.45C9.33807 5.45 9.45 5.33807 9.45 5.2C9.45 5.06193 9.33807 4.95 9.2 4.95"
      />
    </g>
  </svg>
);

export const SadFaceIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="-0.125 -0.125 14.25 14.25" height={16} width={16} strokeWidth={1.25} {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.7 10.5C4.2 8.7 6.1 7.6 8 8.1C9.1 8.4 10 9.3 10.4 10.5"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.85 5.45C4.71193 5.45 4.6 5.33807 4.6 5.2C4.6 5.06193 4.71193 4.95 4.85 4.95"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.85 5.45C4.98807 5.45 5.1 5.33807 5.1 5.2C5.1 5.06193 4.98807 4.95 4.85 4.95"
    />
    <g>
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.24999 5.45C9.11192 5.45 8.99999 5.33807 8.99999 5.2C8.99999 5.06193 9.11192 4.95 9.24999 4.95"
      />
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.24999 5.45C9.38806 5.45 9.49999 5.33807 9.49999 5.2C9.49999 5.06193 9.38806 4.95 9.24999 4.95"
      />
    </g>
  </svg>
);

export const LogoutIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="0 0 14 14" {...props}>
    <g>
      <path
        d="M9.5,10.5v2a1,1,0,0,1-1,1h-7a1,1,0,0,1-1-1V1.5a1,1,0,0,1,1-1h7a1,1,0,0,1,1,1v2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <line
        x1="6.5"
        y1="7"
        x2="13.5"
        y2="7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></line>
      <polyline
        points="11.5 5 13.5 7 11.5 9"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></polyline>
    </g>
  </svg>
);

export const ChatInfoIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="25" height="26" viewBox="0 0 25 26" fill="none" {...props}>
    <path
      d="M15 15.75H14.25C13.4216 15.75 12.75 15.0784 12.75 14.25V10.5C12.75 10.0858 12.4142 9.75002 12 9.75002H11.25M12.75 0.750019C8.93933 0.744524 5.42568 2.80696 3.5728 6.13683C1.71991 9.4667 1.81953 13.5397 3.833 16.775L0.75 23.25L7.224 20.166C11.0101 22.521 15.8706 22.2279 19.3464 19.4352C22.8222 16.6424 24.1551 11.959 22.6708 7.75461C21.1865 3.55018 17.2087 0.74166 12.75 0.750019ZM12.75 6.37502C12.75 6.16791 12.5821 6.00002 12.375 6.00002C12.1679 6.00002 12 6.16791 12 6.37502C12 6.58213 12.1679 6.75002 12.375 6.75002C12.5821 6.75002 12.75 6.58213 12.75 6.37502Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const StripeIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip0_3654_1910)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4 0C1.79086 0 0 1.79086 0 4V20C0 22.2091 1.79086 24 4 24H20C22.2091 24 24 22.2091 24 20V4C24 1.79086 22.2091 0 20 0H4ZM12.294 8.54402C11.526 8.54402 11.064 8.76602 11.064 9.33002C11.064 9.94864 11.8639 10.2207 12.8563 10.5583C14.4724 11.1081 16.5991 11.8316 16.614 14.508C16.614 17.106 14.538 18.6 11.52 18.6C10.272 18.6 8.91 18.354 7.56 17.772V14.322C8.778 14.988 10.314 15.48 11.52 15.48C12.33 15.48 12.912 15.264 12.912 14.598C12.912 13.9128 12.045 13.5993 10.9985 13.2209C9.40602 12.6451 7.398 11.9191 7.398 9.50402C7.398 6.93602 9.354 5.40002 12.3 5.40002C13.5 5.40002 14.688 5.58602 15.894 6.06602V9.47402C14.79 8.88002 13.398 8.54402 12.294 8.54402Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_3654_1910">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const CopyIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M21.75 5.25C22.5784 5.25 23.25 5.92157 23.25 6.75V21.75C23.25 22.5784 22.5784 23.25 21.75 23.25H6.75C5.92157 23.25 5.25 22.5784 5.25 21.75M2.25 18.75H17.25C18.0784 18.75 18.75 18.0784 18.75 17.25V2.25C18.75 1.42157 18.0784 0.75 17.25 0.75H2.25C1.42157 0.75 0.75 1.42157 0.75 2.25V17.25C0.75 18.0784 1.42157 18.75 2.25 18.75Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ToolOrganizerIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M7.75 19.3501C8.25 19.3501 8.65 19.1501 8.95 18.8501C9.25 18.5501 9.45 18.05 9.45 17.65V12.3501C10.75 11.7501 11.75 10.3501 11.75 8.75005C11.75 7.05005 10.75 5.65005 9.25 5.05005C9.25 5.15005 9.25 5.15005 9.25 5.25005V7.75005C9.25 8.55005 8.55 9.25005 7.75 9.25005C6.95 9.25005 6.25 8.55005 6.25 7.75005V5.25005C6.25 5.15005 6.25 5.15005 6.25 5.05005C4.75 5.65005 3.75 7.05005 3.75 8.75005C3.75 10.3501 4.65 11.6501 6.05 12.3501V17.65C5.95 18.55 6.75 19.3501 7.75 19.3501Z"
    ></path>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M0.75 2.75C0.75 1.65 1.65 0.75 2.75 0.75H21.25C22.35 0.75 23.25 1.65 23.25 2.75V21.25C23.25 22.35 22.35 23.25 21.25 23.25H2.75C1.65 23.25 0.75 22.35 0.75 21.25V2.75Z"
    ></path>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M4.75 14.45H10.75"
    ></path>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M18.75 17.45C18.75 18.45 17.95 19.15 17.05 19.15C16.05 19.15 15.35 18.35 15.35 17.45V11.95H18.75V17.45Z"
    ></path>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M17.05 11.95V5.05005"
    ></path>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      d="M13.75 15.95H20.25"
    ></path>
  </svg>
);

export const WandIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      d="M15.273,10.141,2.316,23.1A1,1,0,0,1,.9,21.684L13.859,8.727"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
    <path
      d="M23.109,7.919c.275,0,.364-.189.2-.408L21.444,5.038,22.431,1.9c.082-.262-.065-.41-.327-.327l-3.142.987L16.489.692c-.219-.165-.4-.076-.408.2L16.018,4.4,13.009,6.141c-.238.138-.218.317.045.4l3.357,1.049,1.049,3.357c.082.263.261.283.4.045L19.6,7.982Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

export const HammerIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="-2 0 27 27" strokeWidth={2} {...props}>
    <path
      d="M14.422,12.04,3.487,22.987a1.75,1.75,0,0,1-2.475-2.474L11.95,9.562"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
    <path
      d="M20.4,14.553c-.782.781-3.516.02-7.226-3.69S8.653,4.374,9.435,3.593L12.263.764l-.007.008c.78-.781,3.567.017,7.277,3.727s4.471,6.445,3.69,7.226l.008-.008Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
    <path
      d="M20.711,5.752l.214-.215A1.75,1.75,0,0,0,18.45,3.062l-.218.218"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

export const MagnetIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="-2 0 26 26" {...props}>
    <path
      d="M21.407 14.613L18.579 11.785"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
    <path
      d="M9.386 2.593L12.215 5.421"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
    <path
      d="M6.912,10.725l6.717-6.718a1,1,0,0,0,0-1.414L12.215,1.178a1,1,0,0,0-1.414,0L3.375,8.6A8.5,8.5,0,0,0,15.4,20.624L22.822,13.2a1,1,0,0,0,0-1.414l-1.415-1.415a1,1,0,0,0-1.414,0l-6.717,6.717a4.5,4.5,0,1,1-6.364-6.363Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

export const CodeBracketSquareIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
    />
  </svg>
);

export const AngleBracketsIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M7.629 4.5L1.189 10.939C0.603424 11.5247 0.603424 12.4743 1.189 13.06L7.629 19.5M16.371 4.5L22.811 10.939C23.3966 11.5247 23.3966 12.4743 22.811 13.06L16.371 19.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MarkdownIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="208" height="128" viewBox="0 0 208 128" {...props}>
    <path d="M30 98V30h20l20 25 20-25h20v68H90V59L70 84 50 59v39zm125 0l-30-33h20V30h20v35h20z" fill="currentColor" />
  </svg>
);

export const PythonIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0.125 0.125 23.75 23.75"
    strokeWidth={1.25}
    height={24}
    width={24}
    {...props}
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.9093 8.01262C21.8189 7.88495 21.6991 7.78084 21.5601 7.70904C21.4211 7.63724 21.2669 7.59985 21.1104 7.6H17.2971V2.88809C17.2976 2.73189 17.2607 2.57785 17.1894 2.43887C17.1181 2.29988 17.0146 2.17999 16.8874 2.08924C16.2734 1.65413 14.9377 1 11.9985 1C9.05934 1 7.72369 1.65413 7.10965 2.08924C6.98197 2.17967 6.87787 2.29942 6.80607 2.43843C6.73427 2.57743 6.69687 2.73164 6.69702 2.88809V7.6H2.88369C2.72724 7.59985 2.57303 7.63724 2.43403 7.70904C2.29502 7.78084 2.17528 7.88495 2.08485 8.01262C1.65267 8.62667 0.998535 9.0608 0.998535 12C0.998535 14.9392 1.65267 15.3733 2.08778 15.9874C2.17821 16.115 2.29796 16.2192 2.43696 16.291C2.57597 16.3628 2.73017 16.4002 2.88662 16.4H6.69996V21.1119C6.69947 21.2681 6.73642 21.4221 6.8077 21.5611C6.87898 21.7001 6.98252 21.82 7.10965 21.9108C7.72369 22.3459 9.05934 23 11.9985 23C14.9377 23 16.2734 22.3459 16.8874 21.9108C17.0151 21.8203 17.1192 21.7006 17.191 21.5616C17.2628 21.4226 17.3002 21.2684 17.3 21.1119V16.4H21.1134C21.2698 16.4002 21.424 16.3628 21.563 16.291C21.702 16.2192 21.8218 16.115 21.9122 15.9874C22.3473 15.3733 23.0015 14.9392 23.0015 12C23.0015 9.0608 22.3444 8.62667 21.9093 8.01262Z"
    />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6.69702 7.6001H11.5096" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M12.4874 16.3999H17.3" />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.69702 16.4001V14.9334C6.69702 13.3133 8.4228 12.0001 10.043 12.0001H13.9541C15.5743 12.0001 17.3 10.6869 17.3 9.06676V7.6001"
    />
    <path
      stroke="currentColor"
      d="M10.043 4.42224C9.90803 4.42224 9.79858 4.3128 9.79858 4.17779C9.79858 4.04279 9.90803 3.93335 10.043 3.93335"
    />
    <path
      stroke="currentColor"
      d="M10.043 4.42224C10.178 4.42224 10.2875 4.3128 10.2875 4.17779C10.2875 4.04279 10.178 3.93335 10.043 3.93335"
    />
    <path
      stroke="currentColor"
      d="M13.954 20.0668C13.819 20.0668 13.7096 19.9573 13.7096 19.8223C13.7096 19.6873 13.819 19.5779 13.954 19.5779"
    />
    <path
      stroke="currentColor"
      d="M13.954 20.0668C14.089 20.0668 14.1985 19.9573 14.1985 19.8223C14.1985 19.6873 14.089 19.5779 13.954 19.5779"
    />
  </svg>
);

export const TypeScriptIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="1.5px" height={24} width={24} {...props}>
    <g>
      <rect
        x={0.75}
        y={0.75}
        width={22.5}
        height={22.5}
        rx={2}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.25,17.25a2.5,2.5,0,0,0,5,0c0-1.38-1-2-2.5-2.5s-2.5-1.12-2.5-2.5a2.5,2.5,0,0,1,5,0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={7}
        y1={9.75}
        x2={13}
        y2={9.75}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={10}
        y1={9.75}
        x2={10}
        y2={19.75}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const JavaScriptIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0.125 0.125 23.75 23.75"
    strokeWidth={1.5}
    height={24}
    width={24}
    {...props}
  >
    <defs />
    <title>{"js-1"}</title>
    <rect
      x={0.749}
      y={0.754}
      width={22.5}
      height={22.5}
      rx={1.5}
      ry={1.5}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5,10.5v6A1.5,1.5,0,0,1,9,18H7.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.5,10.5H15A1.5,1.5,0,0,0,13.5,12c0,2.25,3,2.25,3,4.5A1.5,1.5,0,0,1,15,18H13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CurlIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" width={164.245} height={137.234} viewBox="0 0 123.184 102.926" {...props}>
    <path
      d="M114.102 14.043a4.96 4.96 0 0 1-4.961-4.961 4.958 4.958 0 0 1 4.96-4.96 4.96 4.96 0 1 1 0 9.921M64.833 98.805a4.96 4.96 0 0 1-4.96-4.961 4.958 4.958 0 0 1 4.96-4.961 4.957 4.957 0 0 1 4.957 4.96 4.96 4.96 0 0 1-4.957 4.962M114.102 0a9.082 9.082 0 0 0-9.082 9.082c0 1.07.27 2.066.609 3.02L63.023 85.125c-4.117.863-7.273 4.344-7.273 8.719a9.082 9.082 0 0 0 9.082 9.082c5.012 0 9.078-4.067 9.078-9.082 0-1.008-.27-1.93-.57-2.836l42.82-73.262c3.992-.957 7.024-4.379 7.024-8.664A9.082 9.082 0 0 0 114.102 0"
      style={{
        // fill: "#0c544c",
        fill: "currentColor",
        fillOpacity: 1,
        fillRule: "nonzero",
        stroke: "none",
      }}
    />
    <path
      d="M76.941 14.043a4.96 4.96 0 0 1-4.96-4.961 4.958 4.958 0 0 1 4.96-4.96 4.957 4.957 0 0 1 4.957 4.96 4.96 4.96 0 0 1-4.957 4.961M27.668 98.805a4.96 4.96 0 1 1 0-9.922 4.958 4.958 0 0 1 4.96 4.96 4.96 4.96 0 0 1-4.96 4.962M76.941 0a9.08 9.08 0 0 0-9.082 9.082c0 1.07.27 2.066.61 3.02L25.863 85.125c-4.12.863-7.277 4.344-7.277 8.719a9.082 9.082 0 0 0 18.164 0c0-1.008-.27-1.93-.57-2.836L79 17.746c3.992-.957 7.023-4.379 7.023-8.664 0-5.016-4.07-9.082-9.082-9.082M9.082 29.227a4.963 4.963 0 0 1 4.961 4.96 4.96 4.96 0 1 1-4.96-4.96M9.081 43.27a9.082 9.082 0 0 0 9.082-9.082c0-1.004-.273-1.93-.574-2.836-1.203-3.606-4.5-6.247-8.508-6.247-.64 0-1.203.239-1.809.368C3.156 26.332 0 29.813 0 34.188a9.082 9.082 0 0 0 9.082 9.082M4.121 65.922a4.96 4.96 0 1 1 9.922 0 4.96 4.96 0 0 1-4.961 4.957 4.96 4.96 0 0 1-4.96-4.957m14.042 0c0-1.008-.273-1.93-.574-2.836-1.203-3.606-4.496-6.246-8.508-6.246-.64 0-1.203.238-1.809.363C3.156 58.066 0 61.547 0 65.922c0 5.012 4.066 9.082 9.082 9.082 5.016 0 9.082-4.07 9.082-9.082"
      style={{
        // fill: "#073551",
        fill: "currentColor",
        fillOpacity: 1,
        fillRule: "nonzero",
        stroke: "none",
      }}
    />
  </svg>
);

export const ChatIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" strokeWidth={1.5} height={24} width={24} {...props}>
    <path
      d="M21.75,18.75H11.25l-6,4.5v-4.5h-3a1.5,1.5,0,0,1-1.5-1.5v-15A1.5,1.5,0,0,1,2.25.75h19.5a1.5,1.5,0,0,1,1.5,1.5v15A1.5,1.5,0,0,1,21.75,18.75Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M5.25 7.5L18.75 7.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.25 12L15.75 12" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// https://www.streamlinehq.com/icons/streamline-regular/messages-chat-smileys/messages-speech-bubbles/messages-bubble-square
export const MessageBubbleSquareIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    viewBox="0.125 0.125 23.75 23.75"
    xmlns="http://www.w3.org/2000/svg"
    strokeWidth={1.5}
    height={24}
    width={24}
    {...props}
  >
    <path
      d="M21.75,18.75H11.25l-6,4.5v-4.5h-3a1.5,1.5,0,0,1-1.5-1.5v-15A1.5,1.5,0,0,1,2.25.75h19.5a1.5,1.5,0,0,1,1.5,1.5v15A1.5,1.5,0,0,1,21.75,18.75Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FileLinesTypingIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M5.25 5.24805H18.75M5.25 9.74805H18.75M2.25 23.248H21.75C22.5784 23.248 23.25 22.5765 23.25 21.748V2.24805C23.25 1.41962 22.5784 0.748047 21.75 0.748047H2.25C1.42157 0.748047 0.75 1.41962 0.75 2.24805V21.748C0.75 22.5765 1.42157 23.248 2.25 23.248Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M5.25 14.248H12" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const TextCompletionIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-0.125 -0.125 14.25 14.25"
    height={24}
    width={24}
    strokeWidth={1.25}
    {...props}
  >
    <g>
      <line
        x1={9.5}
        y1={13.5}
        x2={0.5}
        y2={13.5}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={13.5}
        y1={10.5}
        x2={0.5}
        y2={10.5}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={13.5}
        y1={7.5}
        x2={0.5}
        y2={7.5}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x={5}
        y={-4}
        width={4}
        height={13}
        rx={0.5}
        transform="translate(4.5 9.5) rotate(-90)"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

// https://www.streamlinehq.com/icons/streamline-regular/interface-essential/controls/settings-vertical
export const FilterIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M17.0005 11.0001V3.00098M17.0005 11.0001C18.657 11.0001 19.9999 12.3428 20.0001 13.9993C20.0004 15.6558 18.657 16.999 17.0005 16.9995M17.0005 11.0001C16.2047 11.0001 15.4415 11.3163 14.8789 11.8792C14.3163 12.442 14.0004 13.2053 14.0008 14.0011C14.0015 15.6576 15.344 17 17.0005 16.9995M17.0005 20.999V16.9995M7.00154 9.00034L7.00154 20.999M7.00154 9.00034C5.34504 8.99984 4.00162 7.65671 4.00186 6.00021C4.00211 4.34371 5.34503 3.00098 7.00154 3.00098C8.65843 3.00171 10.0012 4.34509 10.0012 6.00198C10.0005 7.65848 8.65804 9.00083 7.00154 9.00034Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronsUpDownIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
    <path
      d="M10 22L16 28L22 22M10 10L16 4L22 10"
      stroke="#18181B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronsDownUpIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
    <path
      d="M10 6L16 12L22 6M10 26L16 20L22 26"
      stroke="#18181B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronDownIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
    <path d="M26 12L16 22L6 12" stroke="#18181B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronUpIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
    <path d="M6 20L16 10L26 20" stroke="#18181B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const MarkerDocumentIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 25" fill="none" {...props}>
    <path
      d="M12.749 12.998H4.49902M8.99902 17.498H4.49902M17.999 8.49799H4.49902M7.50002 24.248H2.24902C1.4206 24.248 0.749023 23.5764 0.749023 22.748V3.24799C0.749023 2.41956 1.4206 1.74799 2.24902 1.74799H17.249C17.6404 1.74829 18.0161 1.90152 18.296 2.17499L21.296 5.05699C21.5856 5.33928 21.749 5.72656 21.749 6.13099V9.99999M16.03 22.657L14.439 24.248H10.499L13.909 20.536M17.621 23.188C17.4804 23.3287 17.2895 23.4078 17.0905 23.4078C16.8915 23.4078 16.7007 23.3287 16.56 23.188L13.378 20.006C13.2373 19.8653 13.1582 19.6745 13.1582 19.4755C13.1582 19.2765 13.2373 19.0857 13.378 18.945L15.441 16.882C15.4799 16.844 15.5228 16.8105 15.569 16.782L20.826 13.436C21.1221 13.247 21.5097 13.2886 21.759 13.536L23.029 14.806C23.2772 15.055 23.3188 15.4431 23.129 15.739L19.786 21C19.7575 21.0462 19.724 21.0891 19.686 21.128L17.621 23.188Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ArrowLeftRightIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    {...props}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="17 11 21 7 17 3"></polyline>
    <line x1="21" x2="9" y1="7" y2="7"></line>
    <polyline points="7 21 3 17 7 13"></polyline>
    <line x1="15" x2="3" y1="17" y2="17"></line>
  </svg>
);

// Project types
export const ToolIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M21.75 9.00006C21.7511 10.1226 21.4722 11.2278 20.9386 12.2154C20.405 13.2031 19.6335 14.042 18.694 14.6564C17.7544 15.2707 16.6765 15.641 15.5577 15.7337C14.439 15.8265 13.3148 15.6388 12.2869 15.1876L7.40624 20.8332C7.39499 20.8463 7.38186 20.8604 7.36968 20.8726C6.807 21.4352 6.04386 21.7513 5.24811 21.7513C4.45237 21.7513 3.68923 21.4352 3.12655 20.8726C2.56388 20.3099 2.24777 19.5467 2.24777 18.751C2.24777 17.9553 2.56388 17.1921 3.12655 16.6294C3.13968 16.6172 3.1528 16.6041 3.16686 16.5929L8.81249 11.7132C8.30667 10.5561 8.13524 9.28048 8.31763 8.03094C8.50001 6.78139 9.02893 5.60797 9.84437 4.64377C10.6598 3.67957 11.7291 2.96319 12.9311 2.57588C14.133 2.18858 15.4194 2.14585 16.6444 2.45256C16.7706 2.48421 16.8865 2.54818 16.9806 2.63819C17.0746 2.72819 17.1436 2.84113 17.1808 2.96589C17.218 3.09066 17.222 3.22295 17.1926 3.34976C17.1631 3.47656 17.1012 3.59352 17.0128 3.68912L13.5 7.50006L14.0306 9.97037L16.5 10.5001L20.3109 6.98256C20.4065 6.8942 20.5235 6.83224 20.6503 6.80278C20.7771 6.77333 20.9094 6.77739 21.0342 6.81456C21.1589 6.85173 21.2719 6.92074 21.3619 7.0148C21.4519 7.10885 21.5158 7.22472 21.5475 7.351C21.6821 7.89035 21.7501 8.44416 21.75 9.00006Z"
      fill="currentColor"
    />
  </svg>
);

export const ToolIconOutline = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3748 8.35349C14.61 7.11884 14.9156 5.23186 14.1332 3.67049L12.0623 5.73899C11.7206 6.08058 11.1668 6.08058 10.8251 5.73899L10.3584 5.27232C10.1942 5.10821 10.1019 4.88557 10.1019 4.65341C10.1019 4.42125 10.1942 4.1986 10.3584 4.03449L12.4584 1.93449C10.9161 1.09236 9.00677 1.3402 7.73057 2.5482C6.45438 3.7562 6.10216 5.64904 6.85841 7.23524L1.69999 12.3902C1.53107 12.5589 1.43616 12.7878 1.43616 13.0266C1.43616 13.2653 1.53107 13.4943 1.69999 13.663L2.33641 14.3C2.50522 14.4689 2.73427 14.5639 2.97311 14.5639C3.21196 14.5639 3.441 14.4689 3.60982 14.3L8.76416 9.14566C10.3137 9.88512 12.161 9.56773 13.3748 8.35349Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AgentIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M18.75 4.5H12.75V1.5C12.75 1.30109 12.671 1.11032 12.5303 0.96967C12.3897 0.829018 12.1989 0.75 12 0.75C11.8011 0.75 11.6103 0.829018 11.4697 0.96967C11.329 1.11032 11.25 1.30109 11.25 1.5V4.5H5.25C4.45435 4.5 3.69129 4.81607 3.12868 5.37868C2.56607 5.94129 2.25 6.70435 2.25 7.5V18C2.25 18.7956 2.56607 19.5587 3.12868 20.1213C3.69129 20.6839 4.45435 21 5.25 21H18.75C19.5456 21 20.3087 20.6839 20.8713 20.1213C21.4339 19.5587 21.75 18.7956 21.75 18V7.5C21.75 6.70435 21.4339 5.94129 20.8713 5.37868C20.3087 4.81607 19.5456 4.5 18.75 4.5ZM16.125 9C16.3475 9 16.565 9.06598 16.75 9.1896C16.935 9.31321 17.0792 9.48891 17.1644 9.69448C17.2495 9.90005 17.2718 10.1262 17.2284 10.3445C17.185 10.5627 17.0778 10.7632 16.9205 10.9205C16.7632 11.0778 16.5627 11.185 16.3445 11.2284C16.1262 11.2718 15.9 11.2495 15.6945 11.1644C15.4889 11.0792 15.3132 10.935 15.1896 10.75C15.066 10.565 15 10.3475 15 10.125C15 9.82663 15.1185 9.54048 15.3295 9.3295C15.5405 9.11853 15.8266 9 16.125 9ZM9 17.25H7.5C7.10218 17.25 6.72064 17.092 6.43934 16.8107C6.15804 16.5294 6 16.1478 6 15.75C6 15.3522 6.15804 14.9706 6.43934 14.6893C6.72064 14.408 7.10218 14.25 7.5 14.25H9V17.25ZM7.875 11.25C7.6525 11.25 7.43499 11.184 7.24998 11.0604C7.06498 10.9368 6.92078 10.7611 6.83564 10.5555C6.75049 10.35 6.72821 10.1238 6.77162 9.90552C6.81502 9.68729 6.92217 9.48684 7.0795 9.3295C7.23684 9.17217 7.43729 9.06502 7.65552 9.02162C7.87375 8.97821 8.09995 9.00049 8.30552 9.08564C8.51109 9.17078 8.68679 9.31498 8.8104 9.49998C8.93402 9.68499 9 9.9025 9 10.125C9 10.4234 8.88147 10.7095 8.6705 10.9205C8.45952 11.1315 8.17337 11.25 7.875 11.25ZM13.5 17.25H10.5V14.25H13.5V17.25ZM16.5 17.25H15V14.25H16.5C16.8978 14.25 17.2794 14.408 17.5607 14.6893C17.842 14.9706 18 15.3522 18 15.75C18 16.1478 17.842 16.5294 17.5607 16.8107C17.2794 17.092 16.8978 17.25 16.5 17.25Z"
      fill="currentColor"
    />
  </svg>
);

export const ChainIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9.00001 15L15 9.00004M10.5 7.13535L13.3181 4.32285C14.1644 3.49029 15.3053 3.02584 16.4924 3.03068C17.6795 3.03551 18.8166 3.50923 19.6561 4.34866C20.4955 5.18808 20.9692 6.3252 20.9741 7.51232C20.9789 8.69943 20.5144 9.84037 19.6819 10.6866L16.8638 13.5M7.13532 10.5L4.32282 13.3182C3.49026 14.1644 3.02581 15.3053 3.03065 16.4924C3.03548 17.6796 3.5092 18.8167 4.34863 19.6561C5.18805 20.4955 6.32517 20.9693 7.51229 20.9741C8.6994 20.9789 9.84034 20.5145 10.6866 19.6819L13.5 16.8638"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ModelIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M3.00466 12.0004L3.01057 12.4149L3.0295 13.1903L3.05792 13.8943L3.09583 14.5269L3.11834 14.8163L3.14947 15.1762L3.21114 15.8011L3.28555 16.3765L3.38893 16.995L3.45654 17.3494L3.51364 17.6367L3.62121 18.1363L3.74268 18.5927L3.90536 19.0738L4.01069 19.345L4.08917 19.538L4.20175 19.7973L4.2792 19.9547L4.36323 20.1035L4.45784 20.2469L4.56701 20.3881L4.69473 20.5303L4.7668 20.6027L4.81903 20.652L4.92469 20.7413L5.03268 20.8182L5.14384 20.882L5.259 20.9324L5.37901 20.9689L5.50471 20.991L5.63693 20.9983L5.70575 20.9962L5.78697 20.9895L5.93975 20.9652L6.08204 20.9266L6.21667 20.8743L6.34648 20.8089L6.47431 20.7311L6.66854 20.5922L6.80382 20.4858L6.90526 20.4026L7.08815 20.2428L7.24956 20.088L7.39577 19.9337L7.60025 19.6932L7.87805 19.3316L8.03323 19.123L8.23848 18.842L8.58067 18.343L8.87825 17.8682L9.18697 17.3459L9.36293 17.0445L9.57082 16.6851L9.92197 16.0557L10.3953 15.1603L10.7593 14.4593L11.1491 13.7013L11.8045 12.3888L13.0503 9.90801L13.403 9.2243L13.61 8.83222L13.9675 8.15872L14.4352 7.29988L14.7848 6.69848L14.9924 6.35574L15.1633 6.07663L15.4644 5.59309L15.7568 5.15577L16.0953 4.70064L16.2991 4.44637L16.4484 4.26419L16.7186 3.94828L16.9191 3.74148L17.0624 3.61213L17.2203 3.48598L17.3986 3.35991L17.4971 3.29594L17.6296 3.21613L17.8229 3.11738L17.9505 3.06692L18.0789 3.0299L18.2095 3.00727L18.344 3L18.4838 3.00906L18.5562 3.02002L18.6277 3.0349L18.7615 3.07502L18.8845 3.12862L18.9986 3.19531L19.1056 3.27467L19.2072 3.36629L19.3535 3.52584L19.4496 3.64635L19.5184 3.7386L19.6393 3.91498L19.7416 4.08458L19.8295 4.25223L19.9429 4.5106L20.0816 4.89335L20.1547 5.11199L20.2501 5.40883L20.3972 5.9318L20.5067 6.42377L20.6029 6.96067L20.6537 7.2697L20.7118 7.63911L20.8 8.28288L20.8617 8.88059L20.9104 9.52911L20.934 9.90269L20.9572 10.304L20.988 11.0014L21 11.6463L20.9958 12.3445L20.9884 12.7464L20.9792 13.1385L20.9553 13.8191L20.9172 14.4473L20.8568 15.1254L20.8157 15.5152L20.7773 15.8591L20.703 16.4563L20.6161 17.0055L20.4974 17.5943L20.4201 17.9309L20.3571 18.195L20.2385 18.6549L20.1415 18.9691L20.0659 19.1778L19.9773 19.3945L19.8719 19.6266L19.8117 19.7507L19.7277 19.9179L19.6049 20.1435L19.52 20.2793L19.4285 20.4054L19.327 20.5231L19.2118 20.6342L19.0796 20.7403L19.0059 20.7918L18.948 20.8287L18.8315 20.8916L18.7137 20.9399L18.594 20.9737L18.4722 20.9932L18.3479 20.9985L18.2205 20.9895L18.0898 20.9665L18.023 20.9497L17.934 20.9233L17.7699 20.8632L17.621 20.7931L17.483 20.7125L17.352 20.6209L17.2237 20.5181L17.0274 20.3416L16.8878 20.2085L16.7845 20.1074L16.5983 19.916L16.434 19.7343L16.2851 19.5563L16.0762 19.2832L15.7901 18.8771L15.6292 18.6426L15.4228 18.3374L15.0775 17.7985L14.6225 17.0213L14.2788 16.4094L14.0709 16.0359L13.7187 15.3831L13.2406 14.457L12.8708 13.7332L12.4829 12.969L11.8265 11.6482L11.4408 10.8829L11.0666 10.1436L10.5852 9.19676L10.2326 8.52795L10.0249 8.14484L9.67465 7.50318L9.21352 6.68663L8.86606 6.11753L8.65901 5.79418L8.49337 5.53888L8.20039 5.09665L7.91366 4.69945L7.57936 4.29196L7.37756 4.06705L7.23379 3.91161L7.03442 3.70493L6.90504 3.58246L6.77263 3.47039L6.63218 3.36712L6.47867 3.27105L6.30709 3.18059L6.21296 3.13696L6.14684 3.10891L6.01662 3.062L5.88855 3.02785L5.762 3.00713L5.63636 3.00048L5.51099 3.00854L5.38526 3.03198L5.25856 3.07143L5.19464 3.09737L5.12125 3.13161L4.98729 3.20708L4.86805 3.29224L4.76069 3.38748L4.66236 3.49324L4.57023 3.60993L4.43743 3.80639L4.34836 3.95224L4.28405 4.0626L4.17126 4.27052L4.07629 4.46663L3.99518 4.65723L3.89083 4.94658L3.76325 5.37192L3.6955 5.6157L3.61006 5.93537L3.47844 6.49598L3.38117 7.02054L3.29672 7.59172L3.25255 7.92062L3.20375 8.30231L3.13078 8.96675L3.08188 9.58279L3.04657 10.2507L3.03073 10.6354L3.01621 11.0383L3 11.738L3.00108 12.3849L3.01969 13.0846L3.03563 13.4874L3.05278 13.8704L3.09032 14.5354L3.14134 15.1487L3.21681 15.81L3.26714 16.1899L3.31242 16.5156L3.39886 17.0814L3.49815 17.6008L3.63233 18.1554L3.71939 18.4715L3.78805 18.7112L3.91743 19.1295L4.02325 19.4139L4.10546 19.6008L4.20167 19.7926L4.31587 19.9955L4.38096 20.103L4.47111 20.2446L4.60603 20.4351L4.69969 20.5479L4.79942 20.6494L4.90793 20.7401L5.02788 20.82L5.16196 20.8895L5.23513 20.9204L5.30037 20.944L5.4292 20.9786L5.55643 20.9971L5.68278 21L5.80901 20.9882L5.93583 20.9624L6.06398 20.9234L6.19419 20.8719L6.2603 20.8416L6.35529 20.7946L6.52814 20.6981L6.68245 20.597L6.82339 20.4893L7.02101 20.3116L7.28591 20.0345L7.43057 19.8746L7.63274 19.6446L7.96778 19.229L8.25546 18.825L8.5498 18.3758L8.71635 18.1164L8.92351 17.7897L9.27135 17.215L9.73361 16.391L10.0851 15.7438L10.2927 15.359L10.6454 14.6874L11.1274 13.737L11.5023 12.9951L11.8875 12.23L12.5439 10.9099L12.9321 10.1464L13.301 9.42602L13.7784 8.5046L14.1305 7.85537L14.3384 7.48401L14.6807 6.87824L15.1345 6.10929L15.4793 5.57671L15.6855 5.27536L15.8455 5.04511L16.1302 4.64621L16.3384 4.37849L16.4869 4.20431L16.6507 4.02692L16.8364 3.84063L16.9395 3.74236L17.0782 3.61369L17.2741 3.44329L17.4023 3.34444L17.5331 3.25711L17.6705 3.18109L17.8184 3.11615L17.9808 3.06207L18.0687 3.03903L18.1357 3.02457L18.2666 3.0064L18.3938 3.00255L18.5175 3.01298L18.6384 3.03768L18.7567 3.07661L18.873 3.12974L18.9876 3.19706L19.0444 3.23603L19.118 3.29109L19.2497 3.40316L19.3639 3.51933L19.4643 3.64145L19.5544 3.77133L19.638 3.91082L19.7591 4.14206L19.8421 4.31357L19.9015 4.44025L20.0054 4.67688L20.0927 4.89749L20.1672 5.10967L20.2628 5.42871L20.3795 5.89534L20.4416 6.16339L20.5173 6.50311L20.6335 7.09711L20.7184 7.65088L20.7906 8.25289L20.8278 8.59964L20.8674 8.99083L20.9254 9.67125L20.9613 10.3015L20.9829 10.9843L20.9908 11.3776L20.9968 11.7791L20.9986 12.4765L20.9844 13.1208L20.9512 13.8173L20.9265 14.218L20.9017 14.5895L20.8508 15.2344L20.787 15.8287L20.6963 16.4686L20.6367 16.8357L20.5848 17.1413L20.4866 17.6722L20.3752 18.1585L20.2255 18.6748L20.1285 18.9676L20.0545 19.182L19.9139 19.5576L19.7989 19.8108L19.71 19.9745L19.6064 20.1395L19.4843 20.3104L19.4149 20.3995L19.3175 20.5158L19.1688 20.669L19.0655 20.7562L18.9571 20.831L18.842 20.8928L18.7184 20.9411L18.5849 20.9756L18.5138 20.9875L18.44 20.9958L18.2979 20.9995L18.1621 20.9871L18.0307 20.9595L17.9021 20.9176L17.7744 20.8624L17.581 20.7568L17.4482 20.6726L17.3491 20.6054L17.1699 20.4736L17.0114 20.3426L16.8675 20.209L16.6665 19.9966L16.3951 19.673L16.2448 19.4865L16.0408 19.2276L15.7016 18.7649L15.4083 18.3212L15.106 17.8311L14.9341 17.5482L14.7265 17.2025L14.3766 16.5961L13.908 15.7308L13.5493 15.0525L13.3424 14.6592L12.9898 13.9735L12.5047 13.005L11.7434 11.4894L11.0884 10.1792L10.6982 9.42274L10.3353 8.72599L9.86283 7.8362L9.5119 7.21117L9.30403 6.85429L9.12887 6.55645L8.82138 6.04037L8.52464 5.57161L8.18301 5.07962L7.97798 4.80286L7.82376 4.59857L7.54727 4.24447L7.34351 4.00948L7.19776 3.85924L7.03687 3.7091L6.85465 3.5547L6.7536 3.47455L6.6189 3.37255L6.42488 3.24018L6.29708 3.1667L6.16746 3.10587L6.03343 3.0584L5.89236 3.025L5.74164 3.00636L5.66185 3.00281L5.59259 3.00337L5.46008 3.01589L5.33475 3.04319L5.21563 3.08476L5.10174 3.14011L4.99212 3.20874L4.88578 3.29012L4.78176 3.38378L4.73031 3.43505L4.65869 3.51088L4.53195 3.65887L4.42386 3.80487L4.33039 3.95236L4.2475 4.10483L4.17117 4.26574L4.06013 4.53057L3.98254 4.72783L3.87886 5.00344L3.71875 5.49181L3.59925 5.95424L3.49353 6.46023L3.43744 6.75127L3.37141 7.10832L3.27056 7.73132L3.19823 8.31066L3.13868 8.93972L3.10878 9.30208L3.08735 9.5922L3.05172 10.2261L3.02574 10.9316L3.00942 11.7086L3.00487 12.124"
      stroke="currentColor"
      strokeWidth="2.5"
    />
  </svg>
);

export const FlowIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27 27C27 27 26.9725 26.749 26.9397 26.4616C26.8992 26.106 26.8344 25.5258 26.7596 24.8992C26.6779 24.2144 26.5766 23.3572 26.4616 22.4656C26.3412 21.5317 26.2029 20.4645 26.049 19.3992C25.8918 18.3111 25.7178 17.1325 25.5263 16C25.3346 14.8664 25.1269 13.6876 24.8992 12.6008C24.6757 11.534 24.4372 10.4662 24.1746 9.53437C23.9226 8.64001 23.6571 7.78148 23.3604 7.10082C23.0841 6.46673 22.7936 5.88985 22.4656 5.53839C22.1682 5.21959 21.8303 5.00168 21.5 5.00001C21.1666 4.99832 20.8079 5.21989 20.4741 5.53839C20.1058 5.88987 19.7516 6.46587 19.3992 7.10082C19.0217 7.78102 18.6545 8.63953 18.287 9.53437C17.9045 10.4659 17.5275 11.534 17.1498 12.6008C16.7651 13.6876 16.3833 14.8669 16 16C15.6167 17.1331 15.2349 18.3124 14.8502 19.3992C14.4725 20.466 14.0955 21.5341 13.713 22.4656C13.3455 23.3605 12.9783 24.219 12.6008 24.8992M12.6008 24.8992C12.2484 25.5341 11.8942 26.1101 11.5259 26.4616M12.6008 24.8992C12.2484 25.5341 11.5259 26.4616 11.5259 26.4616M11.5259 26.4616C11.1922 26.7801 10.8334 27.0017 10.5 27C10.1697 26.9983 9.83183 26.7804 9.53436 26.4616C9.20642 26.1102 8.91591 25.5333 8.63956 24.8992C8.34292 24.2185 8.07741 23.36 7.82541 22.4656C7.56284 21.5338 7.32434 20.466 7.10081 19.3992C6.8731 18.3125 6.66544 17.1336 6.47372 16C6.2822 14.8675 6.10818 13.6889 5.951 12.6008C5.79711 11.5355 5.65879 10.4684 5.53838 9.53437C5.42344 8.64286 5.3221 7.78563 5.24038 7.10082C5.16559 6.4742 5.10084 5.89398 5.06026 5.53839C5.02747 5.25106 5 5.00001 5 5.00001"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

export const ArrowPath = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M3.625 7.1241V9.3741H1.375M10.375 4.8741V2.6241H12.625M10.516 2.6226C12.1152 4.28866 12.3263 6.84805 11.0217 8.75362C9.71715 10.6592 7.25473 11.3883 5.123 10.5001M3.4835 9.3756C1.88788 7.7088 1.67889 5.15179 2.98268 3.24802C4.28648 1.34424 6.74614 0.614844 8.877 1.5001"
        stroke="#999999"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const MousePointerIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g id="Left">
        <path
          id="Vector"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.908206 1.39986L5.12221 10.9359C5.18665 11.0815 5.33605 11.1705 5.49481 11.1579C5.65356 11.1453 5.78705 11.0338 5.82771 10.8799L6.76971 7.30086C6.83841 7.0395 7.04241 6.83531 7.30371 6.76636L10.8827 5.82436C11.0367 5.78383 11.1484 5.65041 11.1611 5.49164C11.1738 5.33287 11.0848 5.18339 10.9392 5.11886L1.40321 0.906862C1.26186 0.844565 1.09678 0.875308 0.987336 0.984309C0.877893 1.09331 0.846482 1.25827 0.908206 1.39986Z"
          stroke="#999999"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

export const ArrowRightIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...props}>
      <path
        d="M2.6875 7H12.3125M12.3125 7L8.375 3.0625M12.3125 7L8.375 10.9375"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const TableIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5"
      />
    </svg>
  );
};

export const PythonIconImage = (props: React.ComponentProps<"img">): JSX.Element => {
  return <img src="/assets/providers/python-logomark.svg" alt="Python" className="h-16 w-16" {...props} />;
};

export const VectorIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        id="Vector"
        d="M1.25 8.75V5.375H3.5M9.5 8.75H0.5M3.5 8.75V3.125H6.125M6.125 8.75V0.875H8.75V8.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CaretDown = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
      <path d="M26 12L16 22L6 12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const UploadSimpleIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" {...props}>
      <path
        d="M16 19V5M16 5L11 10M16 5L21 10M27 19V26C27 26.2652 26.8946 26.5196 26.7071 26.7071C26.5196 26.8946 26.2652 27 26 27H6C5.73478 27 5.48043 26.8946 5.29289 26.7071C5.10536 26.5196 5 26.2652 5 26V19"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const SlidersVerticalIcon = (props: React.ComponentProps<"svg">): JSX.Element => {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
      <path
        d="M10 16C11.6569 16 13 17.3431 13 19C13 20.6569 11.6569 22 10 22M10 16C8.34315 16 7 17.3431 7 19C7 20.6569 8.34315 22 10 22M10 16L10 5M10 22V27M22 8C23.6569 8 25 9.34315 25 11C25 12.6569 23.6569 14 22 14M22 8C20.3431 8 19 9.34315 19 11C19 12.6569 20.3431 14 22 14M22 8V5M22 14V27"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
